import { useEditCompanyMemberProfile } from "@/api/console/companies/useEditCompanyMemberProfile";
import InputGroup from "@/components/InputGroup";
import Select from "@/components/Select";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { handleFormErrors } from "@/lib/handleFormErrors";
import toast from "@/lib/toast";
import {
  CompanyUserEdit,
  companyUserEditSchema,
} from "@/schema/console/companies/CompanyUser";
import useMemberDialog from "@/src/console/(auth)/companies/[id]/_hooks/useMemberDialog";
import { SpinnerIosRegular } from "@fluentui/react-icons";
import { zodResolver } from "@hookform/resolvers/zod";
import { useController, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { mutate } from "swr";

interface EditMemberDialogProps {
  companyId: number;
}

function EditMemberDialog({ companyId }: EditMemberDialogProps) {
  const { t } = useTranslation();

  const { type, member, resetDialog } = useMemberDialog();
  const memberId = member?.id ?? -1;

  const methods = useForm<CompanyUserEdit>({
    resolver: zodResolver(companyUserEditSchema),
    values: member,
  });

  const {
    handleSubmit,
    control,
    formState: { errors, isDirty },
    register,
    setError,
  } = methods;

  const { field: roleField } = useController({
    control,
    name: "role",
  });

  const { trigger: editMember, isMutating } = useEditCompanyMemberProfile(
    memberId,
    {
      onSuccess: (data) => {
        resetDialog();
        toast.success(data.message);
        mutate(`/console/companies/${companyId}`);
      },
      onError: (error) => {
        handleFormErrors(error.errors, setError);
      },
    },
  );

  return (
    <Dialog
      open={type === "edit"}
      onOpenChange={(open) => !open && resetDialog()}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("edit-profile")}</DialogTitle>
        </DialogHeader>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleSubmit((data) => {
              console.log(data);
              editMember(data);
            })();
          }}
        >
          <div className=" grid grid-cols-2 gap-6 px-6 pb-5 pt-4">
            <InputGroup
              label={t("name")}
              error={errors.name?.message}
              register={register("name", {
                required: t("name-required"),
              })}
            />
            <InputGroup
              label={t("name-kana")}
              // error={errors.name?.message}
              register={register("name_kana")}
            />
            <InputGroup
              label={t("email")}
              type="email"
              className="col-span-full"
              error={errors.name?.message}
              register={register("email", {
                required: t("email-required"),
              })}
            />
            <InputGroup
              className="col-span-full"
              label={t("phone")}
              type="text"
              register={register("phone_number")}
            />
            <div className="col-span-full">
              <div className="mb-1">{t("role")}</div>
              <Select
                placeholder=""
                className="col-span-2 rounded-md border"
                value={`${roleField.value}`}
                onChange={(value) => roleField.onChange(parseInt(value))}
                options={[
                  { value: "0", label: t("admin") },
                  { value: "1", label: t("member") },
                ]}
              />
              {errors.role?.message && (
                <p className="text-red-500">{errors.role.message}</p>
              )}
            </div>
            <InputGroup
              label={t("position")}
              type="text"
              register={register("position")}
            />
            <InputGroup
              label={t("department")}
              type="text"
              register={register("department")}
            />
            <Button
              className="col-span-full"
              LoadingComponent={<SpinnerIosRegular className="animate-spin" />}
              isLoading={isMutating}
              type="submit"
              disabled={!isDirty}
            >
              {t("submit")}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default EditMemberDialog;
